/**
 * @file receipt-method.ts
 * @description Реестр исходов «способа получения» (phase19.24 шаг 5.1,
 * проект `phase19.24-step5-1-receipt-method-design.md` §2, §6).
 *
 * Один источник правды для трёх экранов: создание заказа, блок согласования
 * доставки в карточке и предпросмотр письма. Подпись, подсказка, обязательные
 * поля, допустимые условия оплаты и сборка тела запроса лежат записями —
 * новый исход = строка в `RECEIPT_METHOD_META`, а не ветка в форме
 * (правило OCP, docs/processes/engineering-principles.md).
 *
 * Тексты ошибок здесь же: одна и та же непонятная форма не должна ругаться
 * по-разному при создании заказа и при согласовании.
 *
 * Правила денег (решение пользователя 2026-09-21, §7 п.1):
 *  - ТК: стоимость обязательна и строго больше нуля (сервер 0 не принимает);
 *  - курьер магазина: 0 разрешён — бесплатная доставка идёт строкой 0 ₽;
 *  - самовывоз: позиции-услуги «Доставка» не бывает вовсе.
 */

import type { DeliveryAgreementReceiptDto } from "./api-delivery-agreement";
import type {
  OrderPaymentTerms,
  OrderReceiptMethod,
  OrderReceiptPayload,
  ShipmentCarrier,
} from "./api-orders";

/** Поля, которые исход требует заполнить. */
export interface ReceiptMethodRequirements {
  carrier: boolean;
  address: boolean;
  /** Сумма доставки обязательна (и, если `minAmount` > 0, не может быть нулевой). */
  amount: boolean;
}

/** Запись реестра: всё, что UI знает об исходе. */
export interface ReceiptMethodMeta {
  /** Подпись радио-карточки: «Самовывоз». */
  title: string;
  /** Вторая строка карточки: чем этот исход отличается. */
  hint: string;
  requires: ReceiptMethodRequirements;
  /** Минимальная сумма доставки в рублях (0 — бесплатная строка разрешена). */
  minAmount: number;
  /** Какие условия оплаты допустимы; первый — по умолчанию. */
  paymentTerms: OrderPaymentTerms[];
  /** Почему недопустимое условие оплаты запрещено — текст под опцией. */
  paymentRestriction: string | null;
  /** Строка «Доставка» в блоке «Итого», когда суммы нет. */
  deliveryNote: string;
}

/**
 * Порядок исходов в форме — он же порядок карточек на экране.
 *
 * Исходов ВЫБОРА три (решение пользователя 2026-09-21): `AGREEMENT` из формы
 * убран. Случай «пока не знаю» — это «Создать черновик»: заказ заводится без
 * письма, а способ выставляется позже в блоке согласования. Радио-вариант
 * «согласуем потом» дублировал черновик и оставлял заказ без предмета
 * согласования.
 */
export const RECEIPT_METHOD_ORDER: OrderReceiptMethod[] = [
  "PICKUP",
  "CARRIER",
  "SHOP_COURIER",
];

/**
 * Исход можно выбрать в форме? `AGREEMENT` приходит только из истории (старые
 * заказы и витринная «доставка по договорённости») — его показываем строкой
 * «способ ещё не выставлен», а не карточкой выбора.
 */
export function isSelectableReceiptMethod(method: OrderReceiptMethod): boolean {
  return RECEIPT_METHOD_ORDER.includes(method);
}

export const RECEIPT_METHOD_META: Record<OrderReceiptMethod, ReceiptMethodMeta> = {
  PICKUP: {
    title: "Самовывоз",
    hint: "Из пункта магазина, бесплатно",
    requires: { carrier: false, address: false, amount: false },
    minAmount: 0,
    // Самовывоз: по умолчанию платят при выдаче, предоплата — по просьбе клиента.
    paymentTerms: ["POSTPAY", "PREPAY"],
    paymentRestriction: null,
    deliveryNote: "не выставляется (самовывоз)",
  },
  CARRIER: {
    title: "Транспортная компания",
    hint: "СДЭК, Почта России, ПЭК, Яндекс, 5Post",
    requires: { carrier: true, address: true, amount: true },
    // Нижняя граница совпадает с сервером: нулевую позицию-услугу в чеке
    // ФЗ-54 не выставляют.
    minAmount: 0.01,
    paymentTerms: ["PREPAY"],
    paymentRestriction: "Доставка ТК — только предоплата (F31)",
    deliveryNote: "не рассчитана",
  },
  SHOP_COURIER: {
    title: "Своими силами",
    hint: "Курьер магазина, своя цена",
    requires: { carrier: false, address: true, amount: true },
    // 0 ₽ разрешён только здесь: бесплатная доставка попадёт в заказ строкой
    // 0 ₽ (решение пользователя 2026-09-21).
    minAmount: 0,
    paymentTerms: ["POSTPAY", "PREPAY"],
    paymentRestriction: null,
    deliveryNote: "не выставлена",
  },
  // Запись осталась только для ЧТЕНИЯ: старые заказы и заявки с витрины уже
  // лежат с этим способом, и подпись им нужна. В карточках выбора его нет
  // (`RECEIPT_METHOD_ORDER`).
  AGREEMENT: {
    title: "Способ ещё не выставлен",
    hint: "Выберите способ получения — без него письмо клиенту не отправить",
    requires: { carrier: false, address: false, amount: false },
    minAmount: 0,
    // Способ неизвестен — денег вперёд не требуем: порядок по умолчанию тот
    // же, что на сервере (`ORDER_RECEIPT_METHODS.AGREEMENT`), иначе форма
    // молча подставляла бы предоплату.
    paymentTerms: ["POSTPAY", "PREPAY"],
    paymentRestriction: null,
    deliveryNote: "определим при согласовании",
  },
};

/** Состояние формы исхода — общий контракт `value`/`onChange` для секций. */
export interface ReceiptFormValue {
  method: OrderReceiptMethod;
  /** Перевозчик для ТК; пустая строка — не выбран. */
  carrier: ShipmentCarrier | "";
  /** Адрес доставки одной строкой (подсказки DaData). */
  address: string;
  /**
   * Город из выбранной подсказки DaData; `null` — адрес набран руками.
   * Едет в снапшот заказа, чтобы расчёт доставки в карточке не открывался с
   * пустым городом (находка ревью 5.1).
   */
  city: string | null;
  /** Стоимость доставки в рублях, строкой из поля ввода. */
  deliveryAmount: string;
  /** «Бесплатно» у курьера магазина: сумма 0 ₽ осознанно, а не пустое поле. */
  freeDelivery: boolean;
  /** Комментарий, который увидит клиент. */
  comment: string;
}

/** Пустая форма исхода; по умолчанию — самовывоз (самый частый случай). */
export function emptyReceiptForm(
  method: OrderReceiptMethod = "PICKUP",
): ReceiptFormValue {
  return {
    method,
    carrier: "",
    address: "",
    city: null,
    deliveryAmount: "",
    freeDelivery: false,
    comment: "",
  };
}

/**
 * Форма из уже сохранённого исхода заявки: блок согласования открывается на
 * том, что клиенту уже предложили, а не на пустом бланке.
 *
 * `null` — исход не выбран: показываем самовывоз как самый частый случай.
 */
export function receiptFormFromDto(
  dto: DeliveryAgreementReceiptDto | null | undefined,
): ReceiptFormValue {
  if (!dto) return emptyReceiptForm();
  const amount = dto.deliveryAmount === null ? null : Number(dto.deliveryAmount);
  return {
    method: dto.method,
    carrier: dto.carrier ?? "",
    address: dto.address ?? "",
    // Город сервер начнёт отдавать вместе с полем в снапшоте; пока его нет —
    // `null`, и повторное сохранение города не теряет (его пришлёт DaData).
    city: dto.city ?? null,
    deliveryAmount: amount === null || Number.isNaN(amount) ? "" : String(amount),
    // Ноль у курьера магазина — это осознанная «бесплатная доставка», а не
    // пустое поле: галочку восстанавливаем, иначе сохранение упрётся в ноль.
    freeDelivery:
      amount === 0 && RECEIPT_METHOD_META[dto.method].minAmount === 0,
    comment: dto.comment ?? "",
  };
}

/** Проблема формы: поле для фокуса и текст человеческим языком. */
export interface ReceiptIssue {
  field: "method" | "carrier" | "address" | "amount" | "pickup";
  message: string;
}

/** Что ещё знает страница, но не знает форма (пункт самовывоза заполнен?). */
export interface ReceiptValidationContext {
  /** Адрес пункта самовывоза заполнен и письмо отправить можно. */
  pickupReady: boolean;
  /**
   * Клиенту прямо сейчас уходит предложение (письмо или диктовка ссылки).
   *
   * Так же, как на сервере (`checkOrderReceipt`, `sending`): сохранить исход
   * самовывоза можно и с незаполненным пунктом — менеджер дособерёт адрес в
   * настройках складов. Нельзя только ОТПРАВИТЬ такое клиенту.
   */
  sending: boolean;
}

/** Рубли из поля ввода: запятая тоже разделитель, пустое — `null`. */
export function parseAmount(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".");
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
}

/** Сумма доставки исхода в рублях; `null` — доставка не выставляется. */
export function receiptDeliveryAmount(value: ReceiptFormValue): number | null {
  const meta = RECEIPT_METHOD_META[value.method];
  if (!meta.requires.amount) return null;
  if (value.freeDelivery && meta.minAmount === 0) return 0;
  return parseAmount(value.deliveryAmount);
}

/**
 * Проверка формы исхода. Возвращает первую проблему — форма короткая, и
 * список ошибок под каждым полем читается хуже, чем одна внятная фраза.
 */
export function validateReceiptForm(
  value: ReceiptFormValue,
  ctx: ReceiptValidationContext,
): ReceiptIssue | null {
  const meta = RECEIPT_METHOD_META[value.method];

  // Исход из истории (`AGREEMENT`): карточки выбора у него нет, и согласовать
  // с клиентом нечего — менеджер обязан выбрать способ.
  if (!isSelectableReceiptMethod(value.method)) {
    return {
      field: "method",
      message: "Выберите способ получения — как именно клиент получит заказ.",
    };
  }
  if (value.method === "PICKUP" && ctx.sending && !ctx.pickupReady) {
    return {
      field: "pickup",
      message:
        "Пункт самовывоза не заполнен: у склада магазина нет адреса. " +
        "Клиент не узнает, куда ехать.",
    };
  }
  if (meta.requires.carrier && !value.carrier) {
    return {
      field: "carrier",
      message: "Выберите перевозчика — клиент должен видеть, кто везёт заказ.",
    };
  }
  if (meta.requires.address && !value.address.trim()) {
    return {
      field: "address",
      message: "Укажите адрес доставки — без него заказ некуда везти.",
    };
  }
  if (meta.requires.amount) {
    const amount = receiptDeliveryAmount(value);
    if (amount === null) {
      return {
        field: "amount",
        message: "Укажите стоимость доставки — она войдёт в сумму заказа.",
      };
    }
    if (amount < meta.minAmount) {
      return {
        field: "amount",
        message:
          meta.minAmount > 0
            ? "Стоимость доставки ТК должна быть больше нуля — иначе позицию не выставить."
            : "Стоимость доставки не может быть отрицательной.",
      };
    }
  }
  return null;
}

/**
 * Что уже лежит в снапшоте заказа: адрес витрины (с кодом ПВЗ, городом и
 * координатами) или прошлый исход согласования.
 */
export interface ReceiptPayloadBaseline {
  address: string | null;
  city?: string | null;
}

/**
 * Тело `receipt` для сервера: пустые поля не отправляем вовсе.
 *
 * `baseline` — адрес, который уже есть в заказе (снапшот чекаута витрины).
 * Менеджер его не трогал — адрес и город НЕ отправляем: сервер сохранит
 * снапшот целиком, вместе с кодом ПВЗ и координатами, которых у формы нет
 * (договорённость с api, ревью 5.1). Тронул — едет то, что на экране.
 */
export function buildReceiptPayload(
  value: ReceiptFormValue,
  baseline?: ReceiptPayloadBaseline | null,
): OrderReceiptPayload {
  const meta = RECEIPT_METHOD_META[value.method];
  const amount = receiptDeliveryAmount(value);
  const addressKept = isAddressUnchanged(value, baseline);
  const address = addressKept ? "" : value.address.trim();
  const comment = value.comment.trim();
  const city = addressKept ? "" : (value.city?.trim() ?? "");
  return {
    method: value.method,
    ...(meta.requires.carrier && value.carrier ? { carrier: value.carrier } : {}),
    ...(meta.requires.address && address ? { address } : {}),
    // Город шлём только из подсказки DaData: разбирать строку самим — значит
    // записать в снапшот заказа улицу вместо города.
    ...(meta.requires.address && city ? { city } : {}),
    // Деньги строкой с двумя знаками — так их принимает сервер
    // (`/^d{1,7}(.d{1,2})?$/`) и так они не теряют копейки в JSON.
    ...(meta.requires.amount && amount !== null
      ? { deliveryAmount: amount.toFixed(2) }
      : {}),
    ...(comment ? { comment } : {}),
  };
}

/**
 * Адрес остался тем же, что пришёл с сервера? Сравниваем и город: смена
 * только города (подсказка DaData уточнила населённый пункт) тоже правка.
 */
function isAddressUnchanged(
  value: ReceiptFormValue,
  baseline?: ReceiptPayloadBaseline | null,
): boolean {
  if (!baseline) return false;
  const sameAddress = value.address.trim() === (baseline.address ?? "").trim();
  const sameCity = (value.city ?? "").trim() === (baseline.city ?? "").trim();
  return sameAddress && sameCity;
}

/** Допустимы ли такие условия оплаты для исхода (F31). */
export function isPaymentTermsAllowed(
  method: OrderReceiptMethod,
  terms: OrderPaymentTerms,
): boolean {
  return RECEIPT_METHOD_META[method].paymentTerms.includes(terms);
}

/**
 * Условия оплаты по умолчанию для исхода. Нужны при переключении карточек:
 * «при получении» на доставке ТК запрещено, и молча оставлять запрещённое
 * значение нельзя — сервер ответит `carrier_requires_prepay`.
 */
export function defaultPaymentTerms(method: OrderReceiptMethod): OrderPaymentTerms {
  return RECEIPT_METHOD_META[method].paymentTerms[0] ?? "PREPAY";
}

/** Подписи условий оплаты — те же слова в форме, письме и карточке. */
export const PAYMENT_TERMS_LABEL: Record<OrderPaymentTerms, string> = {
  PREPAY: "Предоплата",
  POSTPAY: "При получении",
};

/**
 * Исход словами для писем, полосы «Сейчас» и сводки заявки:
 * «Самовывоз», «СДЭК, 450 ₽», «Курьер магазина, бесплатно».
 */
export function receiptSummaryText(
  method: OrderReceiptMethod,
  options: {
    carrierName?: string | null;
    amount?: number | null;
    /** Подпись способа от сервера — она первична: там живёт исходный реестр. */
    label?: string | null;
  } = {},
): string {
  const meta = RECEIPT_METHOD_META[method];
  const amount = options.amount ?? null;
  const money =
    amount === null ? null : amount === 0 ? "бесплатно" : `${formatAmount(amount)} ₽`;
  const head =
    method === "CARRIER" && options.carrierName
      ? options.carrierName
      : (options.label ?? meta.title);
  return money ? `${head}, ${money}` : head;
}

/** «2 400» — тысячи разделяем неразрывным пробелом, как в остальных суммах. */
function formatAmount(value: number): string {
  return value.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}
